import { t } from "i18next";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { GenericListLayout } from "../../components/generic-list-view/GenericListLayout";
import { MachinesTable } from "./sections/MachinesTable";
import { Pagination } from "../../components/pagination/Pagination";
import { api } from "../../services/api/api";
import FilterDrawer from "../../components/filter-drawer/FilterDrawer";
import FiltersDisplay from "../../components/filter/FiltersDisplay";
import MachinesFilterForm from "../customer/filter/MachinesFilterForm";
import EditCreateDrawer from "../../components/edit-create-drawer/EditCreateDrawer";
import MachineForm, { MachineFormValidator } from "../../components/data/machines/MachineForm";
import { validator } from "../../services/form-validator/form-validator";
import {
  searchWithFilters,
  createSingleQuery,
  createTextSearchQuery,
} from "../../services/api/search";
import DeleteMachineModal from "../customer/tabs/modals/DeleteMachineModal";
import { useTablePageSize } from "../../hooks/useTablePageSize";

export function Machines({
  customer,
  hideHeader = false,
  hideFilters = false,
  noPadding = false
}: {
  customer?: EasyTech.Customer;
  hideHeader?: boolean;
  hideFilters?: boolean;
  noPadding?: boolean;
}) {
  const navigate = useNavigate();
  const [machines, setMachines] = useState<EasyTech.Machine[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeFilters, setActiveFilters] = useState<Partial<EasyTech.MachinesFilterType>>({});
  const [sortField, setSortField] = useState("ref");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("ASC");
  const [pageState, setPageState] = useState<number>(0);
  const [pageNumberState, setPageNumberState] = useState<number>(1);
  const [delLoad, setDelLoad] = useState<boolean>(false);
  const [machineToDelete, setMachineToDelete] = useState<EasyTech.Machine | null>(null);

  const pageSize = useTablePageSize();

  /* Un changement de taille invalide le curseur de page : l'élément 181 est en page 9 à 20 par page
   * et en page 2 à 100. Repartir de la première page est le seul comportement qui ne montre jamais
   * une page vide. */
  useEffect(() => { setPageState(0); }, [pageSize]);

  const fetchMachines = useCallback(async (
    appliedFilters = activeFilters,
    orderBy = sortField,
    orderDirection = sortDirection
  ) => {
    setIsLoading(true);
    try {
      const queries = [];
      if (customer) {
        queries.push(createSingleQuery("customer.id", customer.id));
      }
      
      if (appliedFilters.ref) {
        queries.push(createTextSearchQuery(["ref"], appliedFilters.ref));
      }
      
      if (appliedFilters.serialNumber) {
        queries.push(createTextSearchQuery(["serialNumber"], appliedFilters.serialNumber));
      }
      
      if (appliedFilters.releaseYear) {
        queries.push(createSingleQuery("releaseYear", appliedFilters.releaseYear));
      }
      
      if (appliedFilters.type) {
        queries.push(createSingleQuery("type.id", appliedFilters.type.id));
      }
      
      if (appliedFilters.brand) {
        queries.push(createSingleQuery("brand.id", appliedFilters.brand.id));
      }

      const response = await searchWithFilters<EasyTech.Machine>("machines", {
        paginate: {
          pageSize,
          pageNumber: pageState,
        },
        order: {
          by: orderBy,
          direction: orderDirection,
        },
        queries,
      });
      
      setMachines(response.data || []);
      setPageNumberState(response.totalPages || 1);
    } catch (error) {
      console.error("Error fetching machines:", error);
      toast.error(t("An error occurred while fetching machines"));
    } finally {
      setIsLoading(false);
    }
  }, [customer, activeFilters, sortField, sortDirection, pageState, pageSize]);

  useEffect(() => {
    fetchMachines();
  }, [fetchMachines]);

  const handleFilterSubmit = (filterData: Partial<EasyTech.MachinesFilterType>): Promise<void> => {
    return new Promise<void>((resolve) => {
      setActiveFilters(filterData);
      setPageState(0);
      resolve();
    });
  };

  const clearFilter = (field: string): Promise<void> => {
    return new Promise<void>((resolve) => {
      const newFilters = { ...activeFilters };
      delete newFilters[field as keyof typeof newFilters];
      setActiveFilters(newFilters);
      setPageState(0);
      resolve();
    });
  };

  const clearAllFilters = (): Promise<void> => {
    return new Promise<void>((resolve) => {
      setActiveFilters({});
      setPageState(0);
      resolve();
    });
  };

  const handleSort = (head: string, orderDesc: boolean) => {
    setSortField(head);
    setSortDirection(orderDesc ? "DESC" : "ASC");
  };

  const handleMachineCreate = (machine: EasyTech.Machine): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (validator.isFormValid(machine, MachineFormValidator)) {
        api.post("users/machines", {
          ...machine,
          customer: customer ? { id: customer.id } : machine.customer
        }).then(() => {
          toast.success(t("workorder:machines:success:machineAdded"));
          fetchMachines();
          return resolve();
        }).catch((err) => {
          console.error(err);
          toast.error(t("workorder:machines:errors:addMachine"));
          return reject(err);
        });
      } else {
        toast.error(t("workorder:machines:error:invalidFormData"));
        return reject();
      }
    });
  };

  const handleMachineEdit = (machine: EasyTech.Machine): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (validator.isFormValid(machine, MachineFormValidator)) {
        api.put("users/machines/"+machine.id, {
          ...machine,
          customer: customer ? { id: customer.id } : machine.customer
        })
          .then(() => {
            toast.success(t("workorder:machines:success:machineUpdated"));
            fetchMachines();
            return resolve();
          }).catch((err) => {
            console.error(err);
            toast.error(t("workorder:machines:errors:updateMachine"));
            return reject(err);
          });
      } else {
        toast.error(t("workorder:machines:errors:machineLoadError"));
        return reject();
      }
    });
  };

  const transformFiltersForDisplay = (): Record<string, any> => {
    const displayFilters: Record<string, any> = {};
    if (activeFilters.ref) displayFilters.ref = activeFilters.ref;
    if (activeFilters.serialNumber) displayFilters.serialNumber = activeFilters.serialNumber;
    if (activeFilters.releaseYear) displayFilters.releaseYear = activeFilters.releaseYear;
    if (activeFilters.type) displayFilters.type = activeFilters.type.label;
    if (activeFilters.brand) displayFilters.brand = activeFilters.brand.label;
    return displayFilters;
  };

  const headerActions = (
    <EditCreateDrawer<EasyTech.Machine>
      entityName="Machine"
      FormComponent={MachineForm}
      onSubmit={handleMachineCreate}
      data={customer ? ({ customer } as EasyTech.Machine) : undefined}
      isNotEditable={true}
      isNotCreatable={false}
    />
  );

  const filterDrawerComponent = (
    <FilterDrawer<EasyTech.MachinesFilterType>
      FormComponent={MachinesFilterForm}
      onSubmit={handleFilterSubmit}
      entityName="Machines"
      data={activeFilters}
    />
  );

  const filterBadgesComponent = (
    <FiltersDisplay
      filters={transformFiltersForDisplay()}
      onClearFilter={clearFilter}
      onClearAll={clearAllFilters}
      size="sm"
      compact={true}
    />
  );

  return (
    <>
      <GenericListLayout
        entityName={t("client:tabs:machines")}
        isLoading={isLoading}
        headerActions={hideFilters ? null : headerActions}
        filterDrawer={hideFilters ? null : filterDrawerComponent}
        filterBadges={hideFilters ? null : filterBadgesComponent}
        content={
          <MachinesTable
            machines={machines}
            onHeadClick={handleSort}
            orderDesc={sortDirection === "DESC"}
            onEdit={handleMachineEdit}
            onDelete={(m) => { setMachineToDelete(m); setDelLoad(true); }}
            onRowClick={(m) => navigate(`/customers/${m.customer?.id || customer?.id}/machines/${m.id}`)}
            customerId={customer?.id}
          />
        }
        pagination={
          <Pagination
            actualPage={pageState}
            pageNumber={pageNumberState}
            changePage={setPageState}
          />
        }
        showBreadcrumb={!customer}
        hideHeader={hideHeader}
        noPadding={noPadding}
      />
      {delLoad && machineToDelete && (
        <DeleteMachineModal 
          open={delLoad} 
          machine={machineToDelete} 
          onDelete={() => { fetchMachines(); setDelLoad(false); }} 
          onClose={() => setDelLoad(false)} 
        />
      )}
    </>
  );
}
